import React, { ComponentPropsWithoutRef, FC, useState } from 'react';
import { connect } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { RootState } from '@store/reducers';
import { Spinner } from '@components/Spinner/Spinner';
import { FormPageTopPart } from '@components/_shared/FormPageTopPart/FormPageTopPart';
import { ELEMENTS_GAP } from '@utils/spacing';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { currency, normFedTaxId, words } from '@components/_fields/normalizers';
import { updateLoaneeDeal } from '../../../api/updateLoaneeDeal';
import { DataError } from '../../../api/DataError';
import { Alert2 as Alert, cleanSsnToDigitsAndStars, formatCurrency } from 'shared-components';
import { AlertTitle2 as AlertTitle } from 'shared-components';
import {
  Separator,
  Stack,
  Text,
  List,
  Pill,
  useResponsiveBreakpoints,
  Button,
  Card,
  ArrowRight,
  StepCard,
  cn,
} from 'shared-components';
import { PlaidConnectCardContainer } from '@components/Apply/BankConnect/PlaidConnectCard';
import { AVAILABLE_TERM_OPTIONS, setTemp } from '@store/actions';
import { ENDPOINT_SAVE_FILE } from '../../../api/fileSave';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';
import { useApplyWizardContext } from '../../../context/ApplyWizardContext';

import { formValueSelector, reduxForm } from 'redux-form';
import { muiCustomSelect, checkBox, newThemeInput } from '_fields/inputs';
import { Field } from 'redux-form';
import { Form } from '@components/shadcn/components/ui/form';
import {
  ApplyWizardSteps,
  STEP_BCM_SUMMARY,
  STEP_EQ_LEAD_SUMMARY,
  STEP_SUMMARY,
} from '@components/ApplyWizard/ApplyWizardSteps';
import { reduxFormZodValidate } from '@utils/reduxFormZodValidate';
import { useForm } from 'react-hook-form';
import { parseCurrency } from '@components/_fields/parsers';
import { LoanType } from 'src/js/types';
import { required } from '@components/_fields/validation';
import withOnSubmit from '@components/Apply/withOnSubmit/withOnSubmit';
import { isSyntheticTest } from '@utils/platformBasedInfo';
import { SkipConnectCardBCM } from '@components/Apply/BankConnect/SkipConnectCardBCM';
import { flowStepComplete } from '@utils/gaFlow';

export type FundingIsAlmostHereProps = {
  partnerCustomFields: any;

  equipmentDesc: string;
  vendor_url: string;
  ownerEmail: string;
  loanAmount: string;
  loan_id: string;
  business_id: string;
  loanType: LoanType;
  loanTerms: string;
  businessName: string;
  businessRevenue: string;
  businessAddressStreet?: string;
  businessAddressSuite?: string;
  businessAddressCity?: string;
  businessAddressState?: string;
  businessAddressZip?: string;

  yearsInBusiness: string;
  handleSubmit: () => void;
  ein: string;
  invalid: boolean;
  isComplete: boolean;
  validationResult: {
    [key: string]: string;
  };
  user: RootState['user'];
  vendor_name: string;
  typeOfStatements: string;
  haveStatements: boolean;

  owner1First?: string;
  owner1Last?: string;
  owner1Email?: string;
  owner1Phone?: string;
  owner1Ssn?: string;
  owner1DobDay?: string;
  owner1DobMonth?: string;
  owner1DobYear?: string;
  owner1Street?: string;
  owner1Suite?: string;
  owner1City?: string;
  owner1State?: string;
  owner1Zip?: string;
  owner1Title?: string;
  owner1Ownership?: string;

  owner2First?: string;
  owner2Last?: string;
  owner2Email?: string;
  owner2Phone?: string;
  owner2Ssn?: string;
  owner2DobDay?: string;
  owner2DobMonth?: string;
  owner2DobYear?: string;
  owner2Street?: string;
  owner2Suite?: string;
  owner2City?: string;
  owner2State?: string;
  owner2Zip?: string;
  owner2Title?: string;
  owner2Ownership?: string;

  loan_amount: string;
  ownerOneId?: string;
  match: any;
  config: RootState['config'];
  saving?: boolean;

  isNotAnOwner?: boolean;
  owner0First?: string;
  owner0Last?: string;
  owner0Email?: string;
  owner0Phone?: string;
};

export type PillInputProps = {
  labelProps?: ComponentPropsWithoutRef<typeof Text>;
  stackProps?: ComponentPropsWithoutRef<typeof Stack>;
  fieldProps?: ComponentPropsWithoutRef<typeof newThemeInput>;
};

export const AmountPillInput = ({ stackProps, labelProps, fieldProps }: PillInputProps) => {
  return (
    <Stack flow="row" gapPx="10px" {...stackProps}>
      <Text variant="label-info" {...labelProps} className={cn('text-center text-[20px]', labelProps?.className ?? '')}>
        Amount
      </Text>
      <div>
        <Field
          name="loan_amount"
          label="Amount (USD)"
          component={newThemeInput}
          className="amount h-[65px] !max-w-[150px] text-[25px] !rounded-[100px] text-center"
          placeholder="Enter Amount"
          formatAsNumber
          parse={parseCurrency}
          data-testid="loan-amount"
          {...fieldProps}
        />
      </div>
    </Stack>
  );
};

export const BusinessNamePillInput = ({ stackProps, labelProps, fieldProps }: PillInputProps) => {
  return (
    <Stack flow="row" gapPx="10px" {...stackProps}>
      <Text variant="label-info" {...labelProps} className={cn('text-center text-[20px]', labelProps?.className ?? '')}>
        Business Name
      </Text>
      <div>
        <Field
          name="business_name"
          component={newThemeInput}
          type="text"
          placeholder="Company Name*"
          normalize={words}
          label="Business Name*"
          data-testid="business_name"
          className="h-[65px] !max-w-[350px] text-[25px] !rounded-[100px] text-center"
        />
      </div>
    </Stack>
  );
};

export const BusinessDBAPillInput = ({ stackProps, labelProps, fieldProps }: PillInputProps) => {
  return (
    <Stack flow="row" gapPx="10px" {...stackProps}>
      <Text variant="label-info" {...labelProps} className={cn('text-center text-[20px]', labelProps?.className ?? '')}>
        Company DBA (Optional)
      </Text>
      <div>
        <Field
          name="business_dba"
          component={newThemeInput}
          type="text"
          placeholder="Company DBA"
          normalize={words}
          label="Company DBA (Optional)"
          data-testid="business_dba"
          className="h-[65px] !max-w-[350px] text-[25px] !rounded-[100px] text-center"
        />
      </div>
    </Stack>
  );
};

export const TaxIdPillInput = ({ stackProps, labelProps, fieldProps }: PillInputProps) => {
  return (
    <Stack flow="row" gapPx="10px" {...stackProps}>
      <Text variant="label-info" {...labelProps} className={cn('text-center text-[20px]', labelProps?.className ?? '')}>
        Tax ID
      </Text>
      <div>
        <Field
          data-testid="fed-tax-id"
          name="business_ein"
          component={newThemeInput}
          type="tel"
          label="Tax ID"
          placeholder="Federal Tax ID* (XX-XXXXXXX)"
          normalize={normFedTaxId}
          className="h-[65px] !max-w-[180px] text-[25px] !rounded-[100px] text-center"
        />
      </div>
    </Stack>
  );
};

export const RevenuePillInput = ({ stackProps, labelProps, fieldProps }: PillInputProps) => {
  return (
    <Stack flow="row" gapPx="10px" {...stackProps}>
      <Text variant="label-info" {...labelProps} className={cn('text-center text-[20px]', labelProps?.className ?? '')}>
        Revenue
      </Text>
      <div>
        <Field
          name="business_revenue"
          component={newThemeInput}
          labelText="Gross Revenue*"
          parse={parseCurrency}
          formatAsNumber
          labelClassName="infoFormCustomLabel"
          data-testid="business_revenue"
          className="h-[65px] !max-w-[200px] text-[25px] !rounded-[100px] text-center"
        />
      </div>
    </Stack>
  );
};

export const TermsPillInput = ({
  stackProps,
  labelProps,
  loanType,
  config,
}: PillInputProps & { loanType?: LoanType; config: any }) => {
  // TODO: replace AVAILABLE_TERM_OPTIONS with termOptions but it only supports WC and EQ right now.
  return (
    <Stack flow="row" gapPx="10px" {...stackProps}>
      <Text variant="label-info" {...labelProps} className={cn('text-center text-[20px]', labelProps?.className ?? '')}>
        Terms
      </Text>
      <div>
        <Field
          name="loan_terms"
          component={muiCustomSelect}
          autoFocus
          data-testid="terms_select"
          selectTriggerProps={{ className: 'h-[65px] !max-w-[250px] text-[25px] !rounded-[100px] justify-center' }}
          selectValueProps={{ placeholder: 'Terms*' }}
          valueAsNumber
          options={AVAILABLE_TERM_OPTIONS.map((term) => ({
            text: `${term} months`,
            value: Number(term),
          }))}
        />
      </div>
    </Stack>
  );
};

export const TimeInBusinessPillInput = ({ stackProps, labelProps }: PillInputProps) => {
  return (
    <Stack flow="row" gapPx="10px" {...stackProps}>
      <Text variant="label-info" {...labelProps} className={cn('text-center text-[20px]', labelProps?.className ?? '')}>
        Time in Business
      </Text>
      <div>
        <Field
          name="business_years"
          component={muiCustomSelect}
          label="Years in business*"
          className="w100 mui-input-custom mui-select tib-select"
          autoFocus
          valueAsNumber
          data-testid="business_years"
          selectValueProps={{ placeholder: 'Years in Business' }}
          selectTriggerProps={{ className: 'h-[65px] !max-w-[250px] text-[25px] !rounded-[100px] justify-center' }}
          options={[
            { value: 0, text: 'under 1 year' },
            { value: 1, text: '1 year' },
            { value: 2, text: '2 years' },
            { value: 3, text: '3 years' },
            { value: 4, text: '4 years' },
            { value: 5, text: '5 years' },
            { value: 6, text: '6 years' },
            { value: 7, text: '7 years' },
            { value: 8, text: '8 years' },
            { value: 9, text: '9 years' },
            { value: 10, text: '10 years' },
            { value: 11, text: '11 years' },
            { value: 12, text: '12 years' },
            { value: 13, text: '13 years' },
            { value: 14, text: '14 years' },
            { value: 15, text: '15 years' },
            { value: 16, text: '16 years' },
            { value: 17, text: '17 years' },
            { value: 18, text: '18 years' },
            { value: 19, text: '19 years' },
            { value: 20, text: '20+ years' },
          ]}
        />
      </div>
    </Stack>
  );
};

const EQLeadSummaryWrapper1: FC<FundingIsAlmostHereProps> = (props) => {
  const auth0 = useAuth0();
  console.log('EQLeadSummaryWrapper1 props', props);

  if (auth0.isLoading) {
    return (
      <div className="app-section slide infoSLide">
        <Spinner />
      </div>
    );
  }

  return <EQLeadSummaryWrapper2 {...props} />;
};

const ReviewRow = ({
  label,
  value,
  containerProps,
  labelProps,
  valueProps,
}: {
  label: string;
  value: string | number | React.ReactNode;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  labelProps?: React.HTMLAttributes<HTMLDivElement>;
  valueProps?: React.HTMLAttributes<HTMLDivElement>;
}) => {
  return (
    <div
      {...containerProps}
      className={cn(
        'grid align-start grid-cols-1 w640:grid-cols-[120px_1fr] pt-[20px] pb-[10px] px-[0px] mx-[0px] gap-[10px] border-b border-b-[1px] border-b-charcoal',
        containerProps?.className ?? ''
      )}
    >
      <div
        {...labelProps}
        className={cn(
          'w-full grid text-left justify-start align-center text-[14px] font-condensed leading-[120%]',
          labelProps?.className ?? ''
        )}
      >
        {label}
      </div>
      <div
        {...valueProps}
        className={cn(
          'w-full grid justify-start items-center text-[16px] font-standard leading-[100%]',
          valueProps?.className ?? ''
        )}
      >
        {value}
      </div>
    </div>
  );
};

const EQLeadSummaryWrapper2: FC<FundingIsAlmostHereProps> = ({
  handleSubmit,
  partnerCustomFields,
  vendor_url,
  ownerEmail,
  business_id,
  loan_id,
  loanAmount,
  equipmentDesc,
  loanType,
  loanTerms,
  businessName,
  businessRevenue,
  businessAddressStreet,
  businessAddressSuite,
  businessAddressCity,
  businessAddressState,
  businessAddressZip,
  yearsInBusiness,
  saving: savingOnSubmit,
  vendor_name,
  user,
  ein,
  typeOfStatements,
  haveStatements,

  owner1First,
  owner1Last,
  owner1Email,
  owner1Phone,
  owner1Ssn,
  owner1DobDay,
  owner1DobMonth,
  owner1DobYear,
  owner1Street,
  owner1Suite,
  owner1City,
  owner1State,
  owner1Zip,
  owner1Title,
  owner1Ownership,

  owner2First,
  owner2Last,
  owner2Email,
  owner2Phone,
  owner2Ssn,
  owner2DobDay,
  owner2DobMonth,
  owner2DobYear,
  owner2Street,
  owner2Suite,
  owner2City,
  owner2State,
  owner2Zip,
  owner2Title,
  owner2Ownership,

  loan_amount,
  ownerOneId,
  match,
  config,
  invalid,
  validationResult,
  isComplete,

  isNotAnOwner,

  owner0First,
  owner0Last,
  owner0Email,
  owner0Phone,
}) => {
  const rhfForm = useForm();
  const privacyLink = `${vendor_url}/privacy-policy`;
  const termsLink = `${vendor_url}/terms`;

  const loanTypeSafe = Array.isArray(loanType)
    ? loanType.filter((lt) => lt !== 'UNKNOWN').length > 0
      ? loanType
          .filter((lt) => lt !== 'UNKNOWN')[0]
          .toString()
          .toUpperCase()
      : 'UNKNOWN'
    : loanType?.toString().toUpperCase() ?? 'UNKNOWN';
  const { nextRoute, dataLayerPush, saving } = useApplyWizardContext();
  const auth0 = useAuth0();
  const history = useHistory();
  const [{ data: dealUpdateResponse, loading: dealUpdateLoading, error: dealUpdateError }, updateOpportunity] =
    updateLoaneeDeal();
  const [uploadSelected, setUploadSelected] = useState(false);
  const dispatch = useDispatch();

  const isPlaidConnected = typeOfStatements === 'plaid';
  const areStatementsUploaded = typeOfStatements === 'upload';
  const willEmailStatements = typeOfStatements === 'email';
  const responsiveBreakpoints = useResponsiveBreakpoints();

  return (
    <div className="mb-[30px]">
      <Helmet>
        <title>Summary of your application</title>
      </Helmet>
      <Form {...rhfForm}>
        <div className="w-full flex flex-col gap-[10px] mt-[20px]">
          <Text variant="subheadline-sm2" className="!text-[16px] !font-extended py-[20px] !leading-[100%]">
            PLEASE REVIEW YOUR INFORMATION
          </Text>
          <div className="w-full flex flex-col flex-1 gap-[10px] pb-[30px]">
            <Text
              variant="subheadline-sm2"
              className="!text-[20px] !font-standard py-[10px] !leading-[30px] !leading-[120%]"
            >
              Your Information
            </Text>
            {isNotAnOwner ? (
              <>
                <ReviewRow label="NAME" value={`${owner0First} ${owner0Last}`} />
                <ReviewRow label="EMAIL" value={owner0Email} />
                <ReviewRow label="MOBILE NUMBER" value={owner0Phone} />
              </>
            ) : (
              <>
                <ReviewRow label="NAME" value={`${owner1First} ${owner1Last}`} />
                <ReviewRow label="EMAIL" value={owner1Email} />
                <ReviewRow label="MOBILE NUMBER" value={owner1Phone} />
              </>
            )}
          </div>
          <div className="w-full flex flex-col flex-1 gap-[10px] pb-[30px]">
            <Text
              variant="subheadline-sm2"
              className="!text-[20px] !font-standard py-[10px] !leading-[30px] !leading-[120%]"
            >
              Business Information
            </Text>
            <ReviewRow label="BUSINESS NAME" value={businessName} />
            {/* DOES NOT EXIST IN THE FLOW - <ReviewRow label="BUSINESS EMAIL" value={businessEmail} /> */}
            <ReviewRow
              label="BUSINESS ADDRESS"
              value={`${businessAddressStreet}${
                businessAddressSuite ? ` ${businessAddressSuite}` : ''
              }, ${businessAddressCity}, ${businessAddressState} ${businessAddressZip}`}
            />
            <div className="grid align-start grid-cols-1 w640:grid-cols-[1fr_1fr] gap-[10px]">
              <ReviewRow
                label="YEARS IN BUSINESS"
                value={
                  yearsInBusiness == '0'
                    ? 'under 1 year'
                    : yearsInBusiness + (yearsInBusiness == '1' ? ' year' : ' years')
                }
              />
              <ReviewRow
                label="YEARLY REVENUE"
                value={businessRevenue ? formatCurrency(Number(businessRevenue)) : ''}
              />
            </div>
            <ReviewRow label="TAX ID" value={ein} />
          </div>
          <div className="w-full flex flex-col flex-1 gap-[10px] pb-[30px]">
            <Text
              variant="subheadline-sm2"
              className="!text-[20px] !font-standard py-[10px] !leading-[30px] !leading-[120%]"
            >
              Owner Information
            </Text>
            {isNotAnOwner && (
              <>
                <ReviewRow label="NAME" value={`${owner1First} ${owner1Last}`} />
                <ReviewRow label="EMAIL" value={owner1Email} />
                <ReviewRow label="MOBILE NUMBER" value={owner1Phone} />
              </>
            )}
            {owner1Ssn && <ReviewRow label="SSN" value={`***-**-${cleanSsnToDigitsAndStars(owner1Ssn).slice(5, 9)}`} />}
            <div className="grid align-start grid-cols-1 w640:grid-cols-[1fr_1fr_1fr] gap-[10px]">
              <ReviewRow
                label="DAY"
                value={owner1DobDay ? (Number(owner1DobDay) < 10 ? `0${owner1DobDay}` : owner1DobDay) : ''}
              />
              <ReviewRow
                label="MONTH"
                value={owner1DobMonth ? (Number(owner1DobMonth) < 10 ? `0${owner1DobMonth}` : owner1DobMonth) : ''}
              />
              <ReviewRow label="YEAR" value={owner1DobYear} />
            </div>
            <ReviewRow
              label="HOME ADDRESS"
              value={`${owner1Street}${
                owner1Suite ? ` ${owner1Suite}` : ''
              }, ${owner1City}, ${owner1State} ${owner1Zip}`}
            />
            <div className="grid align-start grid-cols-1 w640:grid-cols-[1fr_1fr] gap-[10px]">
              <ReviewRow label="ROLE AT COMPANY" value={owner1Title} />
              <ReviewRow label="% OWNERSHIP" value={owner1Ownership} />
            </div>
          </div>
          {owner2First && owner2Email && (
            <div className="w-full flex flex-col flex-1 gap-[10px] pb-[30px]">
              <Text
                variant="subheadline-sm2"
                className="!text-[20px] !font-standard py-[10px] !leading-[30px] !leading-[120%]"
              >
                Owner #2 Information
              </Text>
              <ReviewRow label="NAME" value={`${owner2First} ${owner2Last}`} />
              <ReviewRow label="EMAIL" value={owner2Email} />
              <ReviewRow label="MOBILE NUMBER" value={owner2Phone} />
              {owner2Ssn && (
                <ReviewRow label="SSN" value={`***-**-${cleanSsnToDigitsAndStars(owner2Ssn).slice(5, 9)}`} />
              )}
              <div className="grid align-start grid-cols-1 w640:grid-cols-[1fr_1fr_1fr] gap-[10px]">
                <ReviewRow
                  label="DAY"
                  value={owner2DobDay ? (Number(owner2DobDay) < 10 ? `0${owner2DobDay}` : owner2DobDay) : ''}
                />
                <ReviewRow
                  label="MONTH"
                  value={owner2DobMonth ? (Number(owner2DobMonth) < 10 ? `0${owner2DobMonth}` : owner2DobMonth) : ''}
                />
                <ReviewRow label="YEAR" value={owner2DobYear} />
              </div>
              <ReviewRow
                label="HOME ADDRESS"
                value={`${owner2Street}${
                  owner2Suite ? ` ${owner2Suite}` : ''
                }, ${owner2City}, ${owner2State} ${owner2Zip}`}
              />
              <div className="grid align-start grid-cols-1 w640:grid-cols-[1fr_1fr] gap-[10px]">
                <ReviewRow label="ROLE AT COMPANY" value={owner2Title} />
                <ReviewRow label="% OWNERSHIP" value={owner2Ownership} />
              </div>
            </div>
          )}
          <div className="w-full flex flex-col flex-1 gap-[10px] pb-[30px]">
            <Text
              variant="subheadline-sm2"
              className="!text-[20px] !font-standard py-[10px] !leading-[30px] !leading-[120%]"
            >
              Your Financing
            </Text>
            <ReviewRow label="FINANCING AMOUNT" value={loanAmount ? formatCurrency(Number(loanAmount)) : ''} />
            <ReviewRow label="EQUIPMENT DESCRIPTION" value={equipmentDesc ?? ''} />
          </div>
          <Stack gapPx="30px">
            <form
              onSubmit={() => {
                flowStepComplete('apply_equipment', 'summary');
                history.push('/eq/decision');
              }}
            >
              <Stack gapPx="40px">
                <div className="consent-box !max-w-[592px] w-full">
                  <Field
                    component={checkBox}
                    id="consent"
                    name="consent"
                    className=" !max-w-[592px]"
                    stackProps={{
                      className: 'w-full !max-w-[592px]',
                    }}
                    variant="transparent-black"
                    data-testid="consent_check"
                    children={
                      <label htmlFor="consent">
                        I agree to the{' '}
                        <a target="_blank" href={termsLink} className="!no-underline !text-moss" tabIndex={-1}>
                          terms and conditions
                        </a>{' '}
                        and{' '}
                        <a target="_blank" href={privacyLink} className="!no-underline !text-moss" tabIndex={-1}>
                          privacy policy
                        </a>
                        .
                      </label>
                    }
                  />
                </div>

                <div className="w-full flex flex-col items-center justify-center gap-[20px] mx-auto">
                  <Button
                    variant="primary-green"
                    color="primary"
                    type="submit"
                    disabled={dealUpdateLoading || invalid || saving}
                    isLoading={dealUpdateLoading || saving || savingOnSubmit}
                    data-testid="info_continue"
                    className="!max-w-[592px] !h-[50px] border-[1px] border-spark2 !py-[12.5px] font-condensed leading-[120%] px-[50px] !text-[16px]"
                  >
                    SUBMIT APPLICATION
                  </Button>
                  <Text
                    variant="body-form-label"
                    className="!text-[16px] !leading-[18px] !font-condensed text-charcoal2"
                  >
                    *NO HARD CREDIT CHECKS
                  </Text>
                </div>
              </Stack>
            </form>
          </Stack>
        </div>
      </Form>
    </div>
  );
};

const EQLeadSummaryFormWrapper = reduxForm<FundingIsAlmostHereProps, FundingIsAlmostHereProps>({
  form: 'application',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  validate: reduxFormZodValidate(ApplyWizardSteps[STEP_EQ_LEAD_SUMMARY].zodSchema),
})(EQLeadSummaryWrapper1);

const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    isComplete: ApplyWizardSteps[STEP_EQ_LEAD_SUMMARY].isComplete(state.form?.application?.values as any),
    formValues: state.form?.application?.values,
    validationResult: reduxFormZodValidate(ApplyWizardSteps[STEP_EQ_LEAD_SUMMARY].zodSchema)(
      state.form?.application?.values
    ),
    business_id: appSelector(state, 'business_id'),
    loan_id: appSelector(state, 'loan_id'),
    partnerCustomFields: state.partnerCustomFields,
    vendor_url: state.brand?.data?.vendor_url,
    ownerOneId: appSelector(state, 'owner_1_id'),
    ownerEmail: appSelector(state, 'owner_1_email'),
    loanAmount: appSelector(state, 'loan_amount'),
    loanType: appSelector(state, 'loan_type') || 'UNKNOWN',
    loanTerms: appSelector(state, 'loan_terms'),
    businessName: appSelector(state, 'business_name'),
    businessRevenue: appSelector(state, 'business_revenue'),
    yearsInBusiness: appSelector(state, 'business_years'),
    ein: appSelector(state, 'business_ein'),
    haveStatements: appSelector(state, 'have_statements'),
    typeOfStatements: appSelector(state, 'type_of_statements'),
    vendor_name: state.brand?.data?.vendor_name,

    owner1First: appSelector(state, 'owner_1_first'),
    owner1Last: appSelector(state, 'owner_1_last'),
    owner1Email: appSelector(state, 'owner_1_email'),
    owner1Phone: appSelector(state, 'owner_1_mobile'),
    owner1Ssn: appSelector(state, 'owner_1_ssn'),
    owner1DobDay: appSelector(state, 'owner_1_dob_day'),
    owner1DobMonth: appSelector(state, 'owner_1_dob_month'),
    owner1DobYear: appSelector(state, 'owner_1_dob_year'),
    owner1Street: appSelector(state, 'owner_1_street'),
    owner1Suite: appSelector(state, 'owner_1_suite'),
    owner1City: appSelector(state, 'owner_1_city'),
    owner1State: appSelector(state, 'owner_1_state'),
    owner1Zip: appSelector(state, 'owner_1_zip'),
    owner1Title: appSelector(state, 'owner_1_title'),
    owner1Ownership: appSelector(state, 'owner_1_ownership'),

    owner2First: appSelector(state, 'owner_2_first'),
    owner2Last: appSelector(state, 'owner_2_last'),
    owner2Email: appSelector(state, 'owner_2_email'),
    owner2Phone: appSelector(state, 'owner_2_mobile'),
    owner2Ssn: appSelector(state, 'owner_2_ssn'),
    owner2DobDay: appSelector(state, 'owner_2_dob_day'),
    owner2DobMonth: appSelector(state, 'owner_2_dob_month'),
    owner2DobYear: appSelector(state, 'owner_2_dob_year'),
    owner2Street: appSelector(state, 'owner_2_street'),
    owner2Suite: appSelector(state, 'owner_2_suite'),
    owner2City: appSelector(state, 'owner_2_city'),
    owner2State: appSelector(state, 'owner_2_state'),
    owner2Zip: appSelector(state, 'owner_2_zip'),
    owner2Title: appSelector(state, 'owner_2_title'),
    owner2Ownership: appSelector(state, 'owner_2_ownership'),

    loan_amount: appSelector(state, 'loan_amount'),

    businessAddressStreet: appSelector(state, 'business_street'),
    businessAddressSuite: appSelector(state, 'business_suite'),
    businessAddressCity: appSelector(state, 'business_city'),
    businessAddressState: appSelector(state, 'business_state'),
    businessAddressZip: appSelector(state, 'business_zip'),

    user: state.user,
    equipmentDesc: appSelector(state, 'equipment_description'),

    isNotAnOwner: appSelector(state, 'isNotAnOwner'),

    owner0First: appSelector(state, 'owner_0_first'),
    owner0Last: appSelector(state, 'owner_0_last'),
    owner0Email: appSelector(state, 'owner_0_email'),
    owner0Phone: appSelector(state, 'owner_0_mobile'),
  };
};

export const EQLeadSummary = connect(mapStateToProps)(EQLeadSummaryFormWrapper);
